/** Druhy položek menu z Magicu — kopíruje typy z `Magic.Framework.Shared/MenuDefinitions/`.
 * `link` = externí URL formulář (WWW odkaz) → otevírá se v nové záložce (nese `url`). */
export type MagicMenuItemKind = 'form' | 'link' | 'sub' | 'click' | 'separator' | 'space' | 'text';

/**
 * Položka menu serializovaná z Magic control instance přes `desktop/menu-ready` postMessage.
 * `menuItemId` je GUID generovaný v paměti Magic instance — Phoenix ho posílá zpět při kliknutí
 * akcí `desktop/click-menu-item` a Magic interně vyvolá příslušnou akci (form/click).
 *
 * Po reloadu Magic control iframu se ID generují nová → Phoenix musí menu re-fetchnout
 * (přirozeně se to stane, protože `desktop/menu-ready` dorazí znovu).
 */
export interface MagicMenuItem {
  readonly menuItemId: string;
  readonly kind: MagicMenuItemKind;
  readonly title?: string;
  /**
   * GlobalName agendy (např. `Person.List`) = hodnota `data-security` z legacy menu.
   * Nese ho jen `form` položka (FormMenuItem.Form.GlobalName); Phoenix z něj mapuje type-ikonu.
   * Volitelné — starší Magic ho neposílá (pak se ikona určí heuristikou z titulku).
   */
  readonly key?: string;
  /**
   * Stabilní, na jazyku NEZÁVISLÝ identifikátor uzlu — explicitní `IMenuItem.ID` (root sekce / submenu
   * z Magic XML, např. `reservations`, `inventory-codebooks`), jinak GlobalName formu. Na rozdíl od
   * `menuItemId` (ephemerální GUID) i `title` (lokalizovaný) přežije reload i změnu kultury → Phoenix
   * z něj páruje native↔legacy sekce/skupiny a deduplikuje. Volitelné (starší Magic ho neposílá).
   */
  readonly stableId?: string;
  /**
   * Hierarchická UNIKÁTNÍ cesta uzlu (řetěz `stableId` předků; u uzlů bez `stableId` index v rodiči,
   * `#3`). Stabilní pro daného uživatele napříč jazyky → vhodná jako React-key / klíč dedupu.
   */
  readonly path?: string;
  /** Absolutní URL pro `kind:'link'` (externí WWW odkaz) — Phoenix ji otevře v nové záložce. */
  readonly url?: string;
  readonly children?: readonly MagicMenuItem[];
}

/** Payload `desktop/menu-ready` zprávy. */
export interface DesktopMenuReadyData {
  readonly items: readonly MagicMenuItem[];
  /**
   * Sdílený resource `revision` hlavního Magic control iframu (cache-busting). Phoenix ho předá
   * jako URL param oknům legacy agend → razítkují scripty stejnou hodnotou (prohlížeč je má v cache).
   */
  readonly revision?: string;
}

/** Payload `form/open` zprávy (Magic → Phoenix). */
export interface FormOpenData {
  readonly globalName: string;
  readonly params?: Record<string, unknown>;
  /** Volitelně — korelační ID pro relay výsledku zpět source iframu (claim navigace). */
  readonly requestId?: string;
}

/** Payload `desktop/user-info` zprávy (Magic → Phoenix) — z control desktopu. */
export interface DesktopUserInfoData {
  /** Zobrazované jméno přihlášeného uživatele. */
  readonly userName?: string;
  /** Absolutní URL helpdesku (Phoenix ji otevře v nové záložce). */
  readonly helpdeskUrl?: string;
  /** Absolutní logout URL (Phoenix na ni naviguje top-okno). */
  readonly logoutUrl?: string;
  /** Smí uživatel měnit heslo. */
  readonly canChangePassword?: boolean;
  /** Verze legacy Magicu (pro patičku vedle verze Phoenixu). */
  readonly magicVersion?: string;
}

/**
 * Viditelnostní flags desktop nástrojů („Nástroje") — payload `desktop/tools-ready` z control desktopu.
 * Logika nástrojů je ve Phoenixu; legacy říká jen, které smí uživatel vidět.
 */
export interface DesktopToolFlags {
  /** Dev režim (Restart/Daemons/Kill). */
  readonly isDevelopment?: boolean;
  /** Config uživatel (Logs, Debug, Debug console). */
  readonly isConfig?: boolean;
  /** Oprávnění vyprázdnit web-content keše. */
  readonly canClearCache?: boolean;
}

/** Akce, kterými Magic adresuje Phoenix (Magic → Phoenix). */
export const MAGIC_TO_PHOENIX_ACTIONS = {
  desktopMenuReady: 'desktop/menu-ready',
  formOpen: 'form/open',
  formResult: 'form/result',
  /** Vycucnutý chrome okna (titulek + akce) z phoenix-single iframu. */
  windowChrome: 'window/chrome',
  /** Klik kamkoli do legacy okna → Phoenix přenese jeho stack do popředí. */
  windowFocus: 'window/focus',
  /** Klávesová zkratka stisknutá v legacy okně → Phoenix dá focus do našeptávače agend. */
  desktopFocusSearch: 'desktop/focus-search',
  /** Info o přihlášeném uživateli + URL (helpdesk, logout) z control desktopu. */
  desktopUserInfo: 'desktop/user-info',
  /** Streamované akce desktop toolbaru (Nástroje) z control desktopu. */
  desktopToolsReady: 'desktop/tools-ready',
  /**
   * Reverse iframe (control / single-form) ztratil Magic session a místo redirectu na LoginForm
   * žádá Phoenix o čerstvý OTT. Phoenix odpoví `auth/ott` (viz PHOENIX_TO_MAGIC_ACTIONS) zpět zdroji.
   */
  authRequired: 'auth/required',
} as const;

/** Akce, kterými Phoenix adresuje Magic (Phoenix → Magic). */
export const PHOENIX_TO_MAGIC_ACTIONS = {
  desktopClickMenuItem: 'desktop/click-menu-item',
  /**
   * Otevři detail objektu dle ObjectCategory + ID (`data: { ObjectCategory, ID }`). Magic přeloží
   * ObjectCategory na editační formulář a probublá zpět jako `form/open` → bridge otevře agendu.
   */
  formEdit: 'form/edit',
  /** Otevři libovolný form dle GlobalName (`data: { Form, ...params }`) → probublá `form/open`. */
  desktopOpenForm: 'desktop/open-form',
  /** Provede desktopovou operaci (`data: { op }`) a vrátí odpověď (debug stav, obsah konzole, …). */
  desktopDevOp: 'desktop/dev-op',
  /** Přepni vizualizaci zámků v konkrétním legacy okně (`data: { enabled }`). */
  windowToggleLocks: 'window/toggle-locks',
  formCloseWindow: 'form/close-window',
  /** Relay výsledku z target iframu zpět source iframu (po claim navigaci s requestId). */
  formResultRelayed: 'form/result-relayed',
  /** Runtime přepnutí dark/light módu v Magic iframu (`data: { enabled }`). */
  darkMode: 'desktop/dark-mode',
  /** Vyžádání invokace akce v hlavičce legacy okna (`data: { actionId }`). */
  windowAction: 'window/action',
  /**
   * Odpověď na `auth/required` – čerstvý OTT (`data: { ott }`) zpět zdrojovému reverse iframu,
   * který se s ním sám reloadne (Magic ho zredeemuje a obnoví FormsAuth cookie).
   */
  authOtt: 'auth/ott',
} as const;

/** Sémantické klíče well-known akcí z hlavičky legacy okna (Phoenix vykreslí vlastní ikonu). */
export type WellKnownActionKey =
  | 'reload'
  | 'lock'
  | 'unlock'
  | 'help'
  | 'copy'
  | 'edit'
  | 'delete'
  | 'add'
  | 'languages'
  | 'detail';

/** Jedna akce „vycucnutá" z hlavičky legacy okna. */
export interface WindowChromeAction {
  /** Stabilní ID pro dispatch zpět (`window/action`). */
  readonly id: string;
  /** Well-known klíč (Phoenix → ikona), nebo prázdné/neznámé → kebab s `label`. */
  readonly key?: string;
  /** Textový popis (z tooltipu legacy tlačítka) — fallback pro neznámé akce. */
  readonly label: string;
  readonly enabled?: boolean;
  readonly toggled?: boolean;
}

/** Payload `window/chrome` zprávy (Magic → Phoenix). */
export interface WindowChromeData {
  readonly title: string;
  readonly actions: readonly WindowChromeAction[];
  /**
   * Okno je v Magicu maximalizované → Phoenix ho vykreslí přes celou plochu (`width`/`height`
   * ignoruje). Když pole chybí (starší Magic), bere se jako maximalizované (zpětná kompatibilita).
   */
  readonly maximized?: boolean;
  /** Zamýšlená šířka okna v px — pro nemaximalizované okno (Phoenix → vycentruje na tuto velikost). */
  readonly width?: number;
  /** Zamýšlená výška okna v px — pro nemaximalizované okno. */
  readonly height?: number;
  /** Identita okna pro info bublinu: „Třída: X" (JS/IRunnable) nebo „Stránka: url" (webform). */
  readonly info?: string;
}

/** Klíč pro requestId v `formParams` legacy agendy — Magic ho zase vyzvedne z URL. */
export const CLAIM_REQUEST_ID_PARAM = '__claimRequestId';

/** Payload `form/result` zprávy. */
export interface FormResultData {
  readonly result?: unknown;
}
